import * as plugins from '../../00plugins.js';
import { demoFunc } from './dees-contextmenu.demo.js';
import {
  customElement,
  html,
  DeesElement,
  property,
  type TemplateResult,
  cssManager,
  css,
  type CSSResult,
  unsafeCSS,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { DeesWindowLayer } from '../dees-windowlayer/dees-windowlayer.js';
import { zIndexLayers } from '../../00zindex.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-contextmenu': DeesContextmenu;
  }
}

export interface IContextMenuOptions {
  /** Actual trigger, including when positioning uses a synthetic event. */
  ownerElement?: HTMLElement;
  /** Related controls that remain interactive while this menu is open, such as
   *  a menubar. Presses elsewhere dismiss without blocking the underlying UI. */
  interactionRoot?: HTMLElement;
}

/** A separator between groups of commands. */
export interface IContextMenuDivider {
  divider: true;
}

/** A command in a context menu. */
export interface IContextMenuItemRegular extends plugins.tsclass.website.IMenuItem {
  shortcut?: string;
  disabled?: boolean;
  /**
   * Marks this item as a choice rather than a command: the menu reserves a
   * check column and announces the item as `menuitemcheckbox` — or, with
   * `radioGroup`, as `menuitemradio`. `undefined` (the default) keeps the item
   * a plain command with nothing announced.
   */
  checked?: boolean;
  /** Name of the set this choice belongs to, where exactly one member is checked. */
  radioGroup?: string;
  submenu?: TContextMenuItem[];
  divider?: never;
}

export type TContextMenuItem = IContextMenuItemRegular | IContextMenuDivider;

@customElement('dees-contextmenu')
export class DeesContextmenu extends DeesElement {
  // DEMO
  public static demo = demoFunc
  public static demoGroups = ['Overlay'];

  // STATIC
  // This will store all the accumulated menu items
  public static contextMenuDeactivated = false;
  public static accumulatedMenuItems: TContextMenuItem[] = [];

  // Add a global event listener for the right-click context menu
  public static initializeGlobalListener() {
    document.addEventListener('contextmenu', (event: MouseEvent) => {
      if (this.contextMenuDeactivated) {
        return;
      }
      event.preventDefault();

      // Clear previously accumulated items
      DeesContextmenu.accumulatedMenuItems = [];

      // Use composedPath to properly traverse shadow DOM boundaries
      const path = event.composedPath();
      
      // Traverse the composed path to accumulate menu items
      for (const element of path) {
        if ((element as any).getContextMenuItems) {
          const items = (element as any).getContextMenuItems();
          if (items && items.length > 0) {
            if (DeesContextmenu.accumulatedMenuItems.length > 0) {
              DeesContextmenu.accumulatedMenuItems.push({ divider: true });
            }
            DeesContextmenu.accumulatedMenuItems.push(...items);
          }
        }
      }

      // Open the context menu with the accumulated items
      DeesContextmenu.openContextMenuWithOptions(event, DeesContextmenu.accumulatedMenuItems);
    });
  }

  // allows opening of a contextmenu with options
  public static async openContextMenuWithOptions(eventArg: MouseEvent, menuItemsArg: TContextMenuItem[], options: IContextMenuOptions = {}) {
    if (this.contextMenuDeactivated) {
      return;
    }
    eventArg.preventDefault();
    eventArg.stopPropagation();
    const contextMenu = new DeesContextmenu();
    contextMenu.returnFocusTarget = options.ownerElement ?? this.deepActiveElement();
    contextMenu.style.position = 'fixed';
    contextMenu.style.zIndex = String(zIndexLayers.overlay.contextMenu);
    contextMenu.style.visibility = 'hidden';
    contextMenu.menuItems = menuItemsArg;
    // Menus don't dim the backdrop (menus float over undimmed content).
    // dismissOnOutsidePress + the 'dees-contextmenu' guard make the menu close on
    // a press anywhere outside the menu chain — robust even when app chrome (e.g.
    // the appbar) paints above the layer and would swallow the overlay's clicks.
    const owner = options.ownerElement ?? eventArg.composedPath()[0] ?? eventArg.target;
    contextMenu.windowLayer = await DeesWindowLayer.createAndShow({
      dimmed: false, dismissOnOutsidePress: true, blocking: !options.interactionRoot,
      ownerElement: owner instanceof Node ? owner : null,
    });
    contextMenu.windowLayer.protectedSelector = 'dees-contextmenu';
    contextMenu.windowLayer.protectedElements = options.interactionRoot ? [options.interactionRoot] : [];
    contextMenu.windowLayer.addEventListener('click', async (event) => {
      // Check if click is on the context menu or its submenus
      const clickedElement = event.target as HTMLElement;
      const isContextMenu = clickedElement.closest('dees-contextmenu');
      if (!isContextMenu) {
        await contextMenu.destroy();
      }
    })
    contextMenu.windowLayer.parentElement!.append(contextMenu);
    
    // Get dimensions after adding to DOM
    await domtools.plugins.smartdelay.delayFor(0);
    const rect = contextMenu.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Calculate position
    let top = eventArg.clientY;
    let left = eventArg.clientX;
    
    // Adjust if menu would go off right edge
    if (left + rect.width > windowWidth) {
      left = windowWidth - rect.width - 10;
    }
    
    // Adjust if menu would go off bottom edge
    if (top + rect.height > windowHeight) {
      top = windowHeight - rect.height - 10;
    }
    
    // Ensure menu doesn't go off left or top edge
    if (left < 10) left = 10;
    if (top < 10) top = 10;
    
    contextMenu.style.top = `${top}px`;
    contextMenu.style.left = `${left}px`;
    // Reveal directly (no opacity/transform animation): a frosted menu's
    // backdrop-filter drops out while an opacity/transform animation runs and
    // snaps back in at the end. Appearing directly keeps the vibrancy stable.
    contextMenu.style.visibility = 'visible';
    contextMenu.focus({ preventScroll: true });
    return contextMenu;
  }

  // INSTANCE
  @property({
    type: Array,
  })
  accessor menuItems: TContextMenuItem[] = [];
  windowLayer!: DeesWindowLayer;
  
  private submenu: DeesContextmenu | null = null;
  private submenuTimeout: any = null;
  private parentMenu: DeesContextmenu | null = null;
  private isDestroying: boolean = false;
  private returnFocusTarget: HTMLElement | null = null;

  private static deepActiveElement(): HTMLElement | null {
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
    return active instanceof HTMLElement ? active : null;
  }

  private ownsFocus(active: HTMLElement | null): boolean {
    return !!active && (active === this || !!this.shadowRoot?.contains(active) || !!this.submenu?.ownsFocus(active));
  }

  public connectedCallback(): Promise<void> {
    if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
    return super.connectedCallback();
  }

  /**
   * STATIC STYLES
   */
  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        transition:
          opacity var(--dees-transition-default) var(--dees-ease-standard),
          transform var(--dees-transition-default) var(--dees-ease-standard),
          left var(--dees-transition-default) var(--dees-ease-spring),
          top var(--dees-transition-default) var(--dees-ease-spring);
        outline: none;
      }

      .mainbox {
        min-width: 200px;
        max-width: 280px;
        background: var(--dees-material-regular-bg);
        -webkit-backdrop-filter: var(--dees-material-regular-filter);
        backdrop-filter: var(--dees-material-regular-filter);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        box-shadow: var(--dees-shadow-lg);
        user-select: none;
        padding: var(--dees-spacing-xs) 0;
        font-size: var(--dees-font-control-size-sm);
        color: var(--dees-color-text-primary);
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        .mainbox {
          background: var(--dees-material-regular-bg-opaque);
        }
      }

      @media (prefers-reduced-transparency: reduce) {
        .mainbox {
          background: var(--dees-material-regular-bg-opaque);
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
        }
      }

      .menuitem {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        cursor: default;
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
        line-height: 1;
        position: relative;
      }

      .menuitem:hover,
      .menuitem:focus {
        background: var(--dees-color-active);
        outline: none;
      }
      
      .menuitem.has-submenu::after {
        content: '›';
        position: absolute;
        right: var(--dees-spacing-sm);
        font-size: 16px;
        color: var(--dees-color-text-secondary);
      }

      .menuitem:active:not(.has-submenu) {
        background: var(--dees-color-pressed);
      }
      
      .menuitem.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      .menuitem dees-icon {
        font-size: 14px;
        color: var(--dees-color-text-secondary);
      }

      /* Choices reserve one check column, so a checked and an unchecked label
         start at the same place. */
      .menuitem-check {
        display: inline-flex;
        flex: 0 0 14px;
        width: 14px;
        align-items: center;
        justify-content: center;
      }

      .menuitem-check dees-icon {
        color: var(--dees-color-text-primary);
      }

      .menuitem-text {
        flex: 1;
      }

      .menuitem-shortcut {
        font-size: var(--dees-font-caption2-size);
        color: var(--dees-color-text-secondary);
        margin-left: auto;
      }

      .menu-divider {
        height: 1px;
        background: var(--dees-color-border-default);
        margin: var(--dees-spacing-xs) 0;
      }

      @media (prefers-reduced-motion: reduce) {
        :host,
        .menuitem {
          transition: none;
        }
      }
    `,
  ];

  /** A menu with choices reserves the check column on every item, so labels stay aligned. */
  private get hasCheckableItems(): boolean {
    return this.menuItems.some((item) => !('divider' in item && item.divider) && item.checked !== undefined);
  }

  public render(): TemplateResult {
    const checkColumn = this.hasCheckableItems;
    return html`
      <div class="mainbox" role="menu">
        ${this.menuItems.map((menuItemArg) => {
          if ('divider' in menuItemArg && menuItemArg.divider) {
            return html`<div class="menu-divider"></div>`;
          }

          const menuItem = menuItemArg;
          const hasSubmenu = !!menuItem.submenu?.length;
          const isCheckable = menuItem.checked !== undefined;
          return html`
            <div
              class="menuitem ${menuItem.disabled ? 'disabled' : ''} ${hasSubmenu ? 'has-submenu' : ''}"
              role=${isCheckable ? (menuItem.radioGroup ? 'menuitemradio' : 'menuitemcheckbox') : 'menuitem'}
              tabindex="-1"
              .ariaChecked=${isCheckable ? String(menuItem.checked) : null}
              aria-disabled=${menuItem.disabled ? 'true' : 'false'}
              @click=${() => {
                if (menuItem.disabled) return;
                if (hasSubmenu) void this.openSubmenuImmediately(menuItem);
                else void this.handleClick(menuItem);
              }}
              @mouseenter=${(event: MouseEvent) => {
                if (menuItem.disabled) return;
                (event.currentTarget as HTMLElement).focus({ preventScroll: true });
                void this.handleMenuItemHover(menuItem, hasSubmenu);
              }}
              @mouseleave=${() => this.handleMenuItemLeave()}
            >
              ${checkColumn ? html`<span class="menuitem-check" aria-hidden="true">
                ${menuItem.checked ? html`<dees-icon .icon="${'lucide:Check'}"></dees-icon>` : ''}
              </span>` : ''}
              ${menuItem.iconName ? html`
                <dees-icon .icon="${menuItem.iconName}"></dees-icon>
              ` : ''}
              <span class="menuitem-text">${menuItem.name}</span>
              ${menuItem.shortcut && !hasSubmenu ? html`
                <span class="menuitem-shortcut">${menuItem.shortcut}</span>
              ` : ''}
            </div>
          `;
        })}
        ${this.menuItems.length === 0 ? html`
            <div class="menuitem" role="menuitem" tabindex="-1" @click=${() => {
              DeesContextmenu.contextMenuDeactivated = true;
              this.destroy();
            }}>
              <dees-icon .icon="lucide:x"></dees-icon>
              <span class="menuitem-text">Allow native context</span>
            </div>
        ` : html``}
      </div>
    `;
  }

  public firstUpdated(changedProperties: Map<string | number | symbol, unknown>): void {
    super.firstUpdated(changedProperties);
    // Focus on the menu for keyboard navigation
    this.focus();
    
    // Add keyboard event listeners
    this.addEventListener('keydown', this.handleKeydown);
  }
  
  private handleKeydown = (event: KeyboardEvent) => {
    const activeItem = this.shadowRoot?.activeElement as HTMLElement | null;
    if (event.key === 'Tab') {
      let root: DeesContextmenu = this;
      while (root.parentMenu) root = root.parentMenu;
      root.returnFocusTarget?.focus({ preventScroll: true });
      void root.destroy();
      return;
    }
    if (event.key === 'ArrowLeft' && this.parentMenu) {
      event.preventDefault();
      event.stopPropagation();
      this.returnFocusTarget?.focus({ preventScroll: true });
      void this.parentMenu.hideSubmenu();
      return;
    }
    if (event.key === 'ArrowRight' && activeItem?.classList.contains('has-submenu')) {
      event.preventDefault();
      event.stopPropagation();
      activeItem.click();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const menuItems = Array.from(this.shadowRoot!.querySelectorAll<HTMLElement>('.menuitem:not(.disabled)'));
    const currentIndex = menuItems.findIndex(item => item === this.shadowRoot!.activeElement);
    
    switch (event.key) {
      case 'ArrowDown':
        if (!menuItems.length) return;
        const nextIndex = currentIndex + 1 < menuItems.length ? currentIndex + 1 : 0;
        menuItems[nextIndex].focus({ preventScroll: true });
        menuItems[nextIndex].dispatchEvent(new MouseEvent('mouseenter'));
        break;
        
      case 'ArrowUp':
        if (!menuItems.length) return;
        const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : menuItems.length - 1;
        menuItems[prevIndex].focus({ preventScroll: true });
        menuItems[prevIndex].dispatchEvent(new MouseEvent('mouseenter'));
        break;
        
      case 'Enter':
        if (currentIndex >= 0) {
          menuItems[currentIndex].click();
        }
        break;
        
      case 'Escape':
        void this.destroyAll();
        break;
    }
  }

  public async handleClick(menuItem: IContextMenuItemRegular) {
    menuItem.action();
    
    // Close all menus in the chain (this menu and all parent menus)
    await this.destroyAll();
  }
  
  private async handleMenuItemHover(menuItem: IContextMenuItemRegular, hasSubmenu: boolean) {
    // Clear any existing timeout
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }
    
    // Hide any existing submenu if hovering a different item
    if (this.submenu) {
      await this.hideSubmenu();
    }
    
    // Show submenu if this item has one
    if (hasSubmenu && menuItem.submenu) {
      this.submenuTimeout = setTimeout(() => {
        this.showSubmenu(menuItem);
      }, 200); // Small delay to prevent accidental triggers
    }
  }
  
  private handleMenuItemLeave() {
    // Add a delay before hiding to allow moving to submenu
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
    }
    
    this.submenuTimeout = setTimeout(() => {
      if (this.submenu && !this.submenu.matches(':hover')) {
        this.hideSubmenu();
      }
    }, 300);
  }
  
  private async openSubmenuImmediately(menuItem: IContextMenuItemRegular) {
    clearTimeout(this.submenuTimeout);
    this.submenuTimeout = null;
    await this.hideSubmenu();
    if (this.isDestroying || !this.isConnected) return;
    await this.showSubmenu(menuItem);
  }

  private async showSubmenu(menuItem: IContextMenuItemRegular) {
    if (this.isDestroying || !this.isConnected || !menuItem.submenu?.length) return;
    
    // Find the menu item element
    const menuItems = Array.from(this.shadowRoot!.querySelectorAll('.menuitem'));
    const menuItemElement = menuItems.find(el => el.querySelector('.menuitem-text')?.textContent === menuItem.name) as HTMLElement;
    if (!menuItemElement) return;
    
    // Create submenu
    this.submenu = new DeesContextmenu();
    const submenu = this.submenu;
    submenu.returnFocusTarget = menuItemElement;
    this.submenu.menuItems = menuItem.submenu;
    this.submenu.parentMenu = this;
    this.submenu.style.position = 'fixed';
    this.submenu.style.zIndex = String(parseInt(this.style.zIndex) + 1);
    this.submenu.style.opacity = '0';
    this.submenu.style.transform = 'scale(0.95)';
    
    // Don't create a window layer for submenus
    this.parentElement!.append(this.submenu);
    
    // Position submenu
    await domtools.plugins.smartdelay.delayFor(0);
    if (this.isDestroying || this.submenu !== submenu || !submenu.isConnected) return;
    const itemRect = menuItemElement.getBoundingClientRect();
    const menuRect = this.getBoundingClientRect();
    const submenuRect = this.submenu.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    
    let left = menuRect.right - 4; // Slight overlap
    let top = itemRect.top;
    
    // Check if submenu would go off right edge
    if (left + submenuRect.width > windowWidth - 10) {
      // Show on left side instead
      left = menuRect.left - submenuRect.width + 4;
    }
    
    // Adjust vertical position if needed
    if (top + submenuRect.height > window.innerHeight - 10) {
      top = window.innerHeight - submenuRect.height - 10;
    }
    
    this.submenu.style.left = `${left}px`;
    this.submenu.style.top = `${top}px`;
    
    // Animate in
    await domtools.plugins.smartdelay.delayFor(0);
    if (this.isDestroying || this.submenu !== submenu || !submenu.isConnected) return;
    this.submenu.style.opacity = '1';
    this.submenu.style.transform = 'scale(1)';
    
    // Handle submenu hover
    this.submenu.addEventListener('mouseenter', () => {
      if (this.submenuTimeout) {
        clearTimeout(this.submenuTimeout);
        this.submenuTimeout = null;
      }
    });
    
    this.submenu.addEventListener('mouseleave', () => {
      this.handleMenuItemLeave();
    });
  }
  
  private async hideSubmenu() {
    if (!this.submenu) return;
    
    await this.submenu.destroy();
    this.submenu = null;
  }

  public async destroy() {
    // Guard against double-destruction
    if (this.isDestroying) {
      return;
    }
    this.isDestroying = true;
    const returnFocus = !this.parentMenu && this.ownsFocus(DeesContextmenu.deepActiveElement())
      ? this.returnFocusTarget : null;

    // Clear timeout
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }

    // Destroy submenu first
    if (this.submenu) {
      await this.submenu.destroy();
      this.submenu = null;
    }

    // Only destroy window layer if this is not a submenu
    // Don't await - let cleanup happen in background for instant visual feedback
    if (this.windowLayer && !this.parentMenu) {
      this.windowLayer.destroy();
    }

    this.style.visibility = 'hidden';

    if (this.parentElement) {
      this.parentElement.removeChild(this);
    }
    // Restore only focus lost by removing this menu. Actions that move focus
    // into a dialog or another control keep their new destination.
    const active = DeesContextmenu.deepActiveElement();
    if (returnFocus?.isConnected && (!active || active === document.body || active instanceof HTMLDialogElement)) {
      returnFocus.focus({ preventScroll: true });
    }
    this.returnFocusTarget = null;
    this.dispatchEvent(new CustomEvent('closed'));
  }
  
  /**
   * Destroys this menu and all parent menus in the chain
   */
  public async destroyAll() {
    // Find the root menu (top-level parent)
    let rootMenu: DeesContextmenu = this;
    while (rootMenu.parentMenu) {
      rootMenu = rootMenu.parentMenu;
    }

    // Destroy from the root - this will cascade through all submenus
    await rootMenu.destroy();
  }
}

DeesContextmenu.initializeGlobalListener();
